import type {
  CatalogSku,
  OrgMemberSummary,
  OrgModelPermissionInheritanceSummary,
  OrgModelPermissionMode,
  OrgModelPermissionWriteEntry,
  OrgUnitSummary,
} from '@nadoutong/contracts';
import { Star } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useCatalog } from '../../api/useCatalog.ts';
import {
  useOrgMemberModelPermissions,
  useOrgUnitModelPermissions,
  useReplaceOrgMemberModelPermissions,
  useReplaceOrgUnitModelPermissions,
} from '../../api/useOrgs.ts';
import { PanelError } from '../../components/PanelError.tsx';
import { useToast } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  Checkbox,
  Drawer,
  IconButton,
  Input,
  RadioGroup,
  Skeleton,
  cx,
} from '../../ui/index.ts';
import { formatTeamApiError, teamTestIdSlug } from './shared.ts';

/**
 * One drawer for both model-permission targets (epic #897 PR 7): a
 * member row or an org-unit row on `/team/permissions`.
 *
 * Since epic #898 P6 (#913) the checklist is SKU-grain: pickable items
 * come from the marketplace catalog (`/v1/catalog/me` `skus[]`, grouped
 * by namespace) and saves WRITE `skuId`-keyed entries. The platform
 * migration folds historical grants into authoritative SKU identities,
 * so this editor never reads or writes channel-keyed permission rows.
 */

export type ModelPermissionTarget =
  | { readonly kind: 'member'; readonly member: OrgMemberSummary }
  | { readonly kind: 'unit'; readonly unit: OrgUnitSummary };

interface PermissionSkuItem {
  readonly skuId: string;
  readonly namespaceSlug: string;
  readonly namespaceName: string;
  readonly skuName: string;
  readonly vendor: string;
  readonly model: string;
  readonly modelLabel: string;
}

interface PermissionNamespaceGroup {
  readonly slug: string;
  readonly displayName: string;
  readonly items: readonly PermissionSkuItem[];
}

interface Props {
  readonly orgId: string;
  readonly target: ModelPermissionTarget;
  readonly onClose: () => void;
}

export function ModelPermissionsDrawer({ orgId, target, onClose }: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const catalogQ = useCatalog();
  const memberQ = useOrgMemberModelPermissions(
    orgId,
    target.kind === 'member' ? target.member.accountId : undefined,
    target.kind === 'member',
  );
  const unitQ = useOrgUnitModelPermissions(
    orgId,
    target.kind === 'unit' ? target.unit.id : undefined,
    target.kind === 'unit',
  );
  const permissionsQ = target.kind === 'member' ? memberQ : unitQ;
  const replaceMemberM = useReplaceOrgMemberModelPermissions();
  const replaceUnitM = useReplaceOrgUnitModelPermissions();
  const saving = replaceMemberM.isPending || replaceUnitM.isPending;

  const isDefaultRootUnit = target.kind === 'unit' && target.unit.isDefaultRoot;
  const targetLabel =
    target.kind === 'member'
      ? (target.member.displayName ?? target.member.email)
      : target.unit.path;
  const fallbackMode: OrgModelPermissionMode =
    target.kind === 'member' ? target.member.modelPermissionMode : target.unit.modelPermissionMode;

  const [mode, setMode] = useState<OrgModelPermissionMode>(isDefaultRootUnit ? 'all' : 'inherit');
  const [search, setSearch] = useState('');
  const [selectedSkuIds, setSelectedSkuIds] = useState<ReadonlySet<string>>(() => new Set());
  const [defaultSkuId, setDefaultSkuId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const groups = useMemo(
    () => buildPermissionNamespaceGroups(catalogQ.data?.skus ?? []),
    [catalogQ.data?.skus],
  );
  const catalogItems = useMemo(() => groups.flatMap((group) => group.items), [groups]);

  const data = permissionsQ.data;
  const directPermissions = useMemo(() => data?.permissions ?? [], [data]);

  useEffect(() => {
    if (data === undefined) return;
    const nextMode = data.mode ?? (directPermissions.length > 0 ? 'custom' : fallbackMode);
    setMode(nextMode);
    const nextSelected = new Set(directPermissions.map((permission) => permission.skuId));
    setSelectedSkuIds(nextSelected);
    const nextDefault = directPermissions.find((permission) => permission.isDefault);
    const nextDefaultSkuId = nextDefault?.skuId ?? null;
    setDefaultSkuId(
      nextDefaultSkuId !== null && nextSelected.has(nextDefaultSkuId) ? nextDefaultSkuId : null,
    );
    setSaveError(null);
  }, [data, directPermissions, fallbackMode]);

  const normalizedSearch = search.trim().toLowerCase();
  const filteredGroups = useMemo(
    () =>
      normalizedSearch.length === 0
        ? groups
        : groups
            .map((group) => ({
              ...group,
              items: group.items.filter((item) => permissionItemMatches(item, normalizedSearch)),
            }))
            .filter((group) => group.items.length > 0),
    [groups, normalizedSearch],
  );

  const effectivePermissions = data?.effectivePermissions ?? directPermissions;
  const inheritedLabel = formatInheritance(
    data?.inheritedFrom ?? null,
    t('team.permissions.drawer.global_source'),
  );
  const effectiveCount =
    mode === 'all'
      ? catalogItems.length
      : mode === 'custom'
        ? selectedSkuIds.size
        : data?.effectiveMode === 'all'
          ? catalogItems.length
          : effectivePermissions.length;
  const defaultItem =
    defaultSkuId === null
      ? null
      : (catalogItems.find((item) => item.skuId === defaultSkuId) ?? null);
  const loading = catalogQ.isLoading || permissionsQ.isLoading;
  const loadFailed = catalogQ.isError || permissionsQ.isError;
  const canSave = !loading && !loadFailed && data !== undefined && !saving;

  function toggleSelection(item: PermissionSkuItem): void {
    setSaveError(null);
    setSelectedSkuIds((current) => {
      const next = new Set(current);
      if (next.has(item.skuId)) {
        next.delete(item.skuId);
        setDefaultSkuId((currentDefault) =>
          currentDefault === item.skuId ? null : currentDefault,
        );
      } else {
        next.add(item.skuId);
      }
      return next;
    });
  }

  function toggleGroup(group: PermissionNamespaceGroup): void {
    setSaveError(null);
    const groupIds = group.items.map((item) => item.skuId);
    const allSelected = groupIds.every((skuId) => selectedSkuIds.has(skuId));
    setSelectedSkuIds((current) => {
      const next = new Set(current);
      for (const skuId of groupIds) {
        if (allSelected) next.delete(skuId);
        else next.add(skuId);
      }
      return next;
    });
    if (allSelected) {
      setDefaultSkuId((currentDefault) =>
        currentDefault !== null && groupIds.includes(currentDefault) ? null : currentDefault,
      );
    }
  }

  async function handleSave(): Promise<void> {
    if (!canSave) return;
    setSaveError(null);
    const skuEntries: OrgModelPermissionWriteEntry[] = catalogItems
      .filter((item) => selectedSkuIds.has(item.skuId))
      .map((item) => ({
        skuId: item.skuId,
        isDefault: item.skuId === defaultSkuId,
      }));
    const body = {
      mode,
      permissions: mode === 'custom' ? skuEntries : [],
    };
    try {
      if (target.kind === 'member') {
        await replaceMemberM.mutateAsync({ orgId, accountId: target.member.accountId, body });
        toast.success(t('team.permissions.toast.member_saved'));
      } else {
        await replaceUnitM.mutateAsync({ orgId, orgUnitId: target.unit.id, body });
        toast.success(t('team.permissions.toast.unit_saved'));
      }
      onClose();
    } catch (err) {
      setSaveError(formatTeamApiError(err, t('team.permissions.drawer.load_error')));
    }
  }

  const modeOptions = (['inherit', 'all', 'custom'] as const)
    .filter((candidate) => !isDefaultRootUnit || candidate !== 'inherit')
    .map((candidate) => ({
      value: candidate,
      label:
        candidate === 'inherit'
          ? t('team.permissions.mode.inherit')
          : candidate === 'all'
            ? t('team.permissions.mode.all')
            : t('team.permissions.mode.custom'),
      hint:
        candidate === 'inherit'
          ? t('team.permissions.mode_help.inherit')
          : candidate === 'all'
            ? t('team.permissions.mode_help.all')
            : t('team.permissions.mode_help.custom'),
      disabled: loading || saving,
    }));

  return (
    <Drawer
      open
      onOpenChange={(open) => {
        if (!open && !saving) onClose();
      }}
      width="lg"
      title={
        target.kind === 'member'
          ? t('team.permissions.drawer.member_title')
          : t('team.permissions.drawer.unit_title')
      }
      description={
        target.kind === 'member'
          ? t('team.permissions.drawer.member_subtitle', { target: targetLabel })
          : t('team.permissions.drawer.unit_subtitle', { target: targetLabel })
      }
      closeLabel={t('team.permissions.drawer.close')}
      testId="team-model-permissions-drawer"
      footer={
        <>
          {saveError !== null ? (
            <p
              role="alert"
              data-testid="team-model-permissions-save-error"
              className="m-0 mr-auto text-label text-bad"
            >
              {saveError}
            </p>
          ) : null}
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
            data-testid="team-model-permissions-cancel"
          >
            {t('team.permissions.drawer.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={saving}
            disabled={!canSave}
            onClick={() => {
              void handleSave();
            }}
            data-testid="team-model-permissions-save"
          >
            {t('team.permissions.drawer.save')}
          </Button>
        </>
      }
    >
      {loading ? (
        <div className="flex flex-col gap-2" data-testid="team-model-permissions-loading">
          {[0, 1, 2].map((idx) => (
            <Skeleton key={idx} className="h-16" />
          ))}
          <span className="sr-only">{t('team.permissions.drawer.loading')}</span>
        </div>
      ) : loadFailed ? (
        <PanelError
          message={formatTeamApiError(
            catalogQ.isError ? catalogQ.error : permissionsQ.error,
            t('team.permissions.drawer.load_error'),
          )}
          retryLabel={t('team.retry')}
          onRetry={() => {
            if (catalogQ.isError) void catalogQ.refetch();
            if (permissionsQ.isError) void permissionsQ.refetch();
          }}
          testId="team-model-permissions-load-error"
        />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            <span className="rounded bg-well px-2.5 py-1 font-mono text-micro text-paper-mid">
              {t('team.permissions.drawer.selected_count', { count: effectiveCount })}
            </span>
            {mode === 'inherit' ? (
              <span className="rounded bg-well px-2.5 py-1 font-mono text-micro text-paper-mid">
                {t('team.permissions.drawer.inherited_from', { source: inheritedLabel })}
              </span>
            ) : null}
            <span className="rounded bg-well px-2.5 py-1 font-mono text-micro text-paper-mid">
              {t('team.permissions.drawer.default_model', { model: defaultItem?.model ?? '--' })}
            </span>
          </div>

          <fieldset className="m-0 flex min-w-0 flex-col gap-1.5 border-0 p-0">
            <legend className="p-0 text-label text-paper-mid">
              {t('team.permissions.drawer.mode_label')}
            </legend>
            <RadioGroup
              value={mode}
              onValueChange={(next) => {
                if (next === 'inherit' || next === 'all' || next === 'custom') {
                  setMode(next);
                  setSaveError(null);
                }
              }}
              options={modeOptions}
              ariaLabel={t('team.permissions.drawer.mode_label')}
              testId="team-model-permissions-mode"
            />
          </fieldset>

          {mode !== 'custom' ? (
            <div
              className="rounded-lg border border-line-soft bg-well/50 p-4"
              data-testid="team-model-permissions-mode-note"
            >
              <p className="m-0 text-dense font-medium text-paper">
                {mode === 'all'
                  ? t('team.permissions.drawer.all_active')
                  : t('team.permissions.drawer.inherit_active', { source: inheritedLabel })}
              </p>
              <p className="m-0 mt-1 text-dense text-paper-low">
                {mode === 'all'
                  ? t('team.permissions.drawer.all_body')
                  : t('team.permissions.drawer.inherit_body')}
              </p>
            </div>
          ) : groups.length === 0 ? (
            <p className="m-0 text-dense text-paper-low" data-testid="team-model-permissions-empty">
              {t('team.permissions.drawer.empty')}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              <Input
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                placeholder={t('team.permissions.drawer.search_placeholder')}
                aria-label={t('team.permissions.drawer.search_label')}
                data-testid="team-model-permissions-search"
              />
              {filteredGroups.length === 0 && normalizedSearch.length > 0 ? (
                <p className="m-0 py-6 text-center text-dense text-paper-low">
                  {t('team.permissions.drawer.no_search')}
                </p>
              ) : (
                filteredGroups.map((group) => {
                  const selectedInGroup = group.items.filter((item) =>
                    selectedSkuIds.has(item.skuId),
                  ).length;
                  const allSelected =
                    group.items.length > 0 && selectedInGroup === group.items.length;
                  return (
                    <section
                      key={group.slug}
                      className="overflow-hidden rounded-lg border border-line-soft"
                    >
                      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line-soft bg-well/50 px-3 py-2">
                        <span className="flex min-w-0 items-baseline gap-2">
                          <span className="truncate text-dense font-medium text-paper">
                            {group.displayName}
                          </span>
                          <span className="font-mono text-micro text-paper-low">{group.slug}</span>
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="font-mono text-micro text-paper-low">
                            {t('team.permissions.drawer.group_count', {
                              selected: selectedInGroup,
                              total: group.items.length,
                            })}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={saving}
                            onClick={() => toggleGroup(group)}
                            data-testid={`team-model-permissions-group-toggle-${teamTestIdSlug(group.slug)}`}
                          >
                            {allSelected
                              ? t('team.permissions.drawer.clear_group')
                              : t('team.permissions.drawer.select_group')}
                          </Button>
                        </span>
                      </header>
                      <ul className="m-0 flex list-none flex-col p-0">
                        {group.items.map((item) => {
                          const checked = selectedSkuIds.has(item.skuId);
                          const isDefault = defaultSkuId === item.skuId;
                          const slug = teamTestIdSlug(item.skuId);
                          return (
                            <li
                              key={item.skuId}
                              className="flex items-center justify-between gap-3 border-b border-line-soft px-3 py-2 last:border-b-0"
                            >
                              <Checkbox
                                checked={checked}
                                disabled={saving}
                                onCheckedChange={() => toggleSelection(item)}
                                label={
                                  <span className="flex min-w-0 flex-col">
                                    <span className="truncate font-mono text-dense text-paper">
                                      {item.model}
                                    </span>
                                    <span className="truncate text-micro text-paper-low">
                                      {item.modelLabel}
                                    </span>
                                  </span>
                                }
                                testId={`team-model-permission-${slug}`}
                              />
                              <IconButton
                                size="sm"
                                label={t('team.permissions.drawer.set_default', {
                                  model: item.model,
                                })}
                                disabled={saving || !checked}
                                aria-pressed={isDefault}
                                onClick={() => {
                                  setSaveError(null);
                                  setDefaultSkuId(isDefault ? null : item.skuId);
                                }}
                                data-testid={`team-model-default-${slug}`}
                              >
                                <Star
                                  aria-hidden="true"
                                  className={cx('h-4 w-4', isDefault && 'fill-warn text-warn')}
                                />
                              </IconButton>
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

function buildPermissionNamespaceGroups(
  skus: readonly CatalogSku[],
): readonly PermissionNamespaceGroup[] {
  const groups = new Map<string, { displayName: string; items: PermissionSkuItem[] }>();
  const seenSkuIds = new Set<string>();
  for (const sku of skus) {
    if (seenSkuIds.has(sku.sku_id)) continue;
    seenSkuIds.add(sku.sku_id);
    const group = groups.get(sku.namespace_slug) ?? {
      displayName: sku.namespace_display_name,
      items: [],
    };
    group.items.push({
      skuId: sku.sku_id,
      namespaceSlug: sku.namespace_slug,
      namespaceName: sku.namespace_display_name,
      skuName: sku.sku_name,
      vendor: sku.vendor,
      model: sku.model,
      modelLabel: sku.model_display_name ?? sku.model,
    });
    groups.set(sku.namespace_slug, group);
  }
  return [...groups.entries()]
    .map(([slug, group]) => ({
      slug,
      displayName: group.displayName,
      items: [...group.items].sort((a, b) => a.model.localeCompare(b.model)),
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

function permissionItemMatches(item: PermissionSkuItem, normalizedSearch: string): boolean {
  return (
    item.namespaceSlug.toLowerCase().includes(normalizedSearch) ||
    item.namespaceName.toLowerCase().includes(normalizedSearch) ||
    item.skuName.toLowerCase().includes(normalizedSearch) ||
    item.vendor.toLowerCase().includes(normalizedSearch) ||
    item.model.toLowerCase().includes(normalizedSearch) ||
    item.modelLabel.toLowerCase().includes(normalizedSearch)
  );
}

function formatInheritance(
  inheritedFrom: OrgModelPermissionInheritanceSummary | null,
  globalLabel: string,
): string {
  if (inheritedFrom === null || inheritedFrom.source === 'global') {
    return globalLabel;
  }
  return inheritedFrom.orgUnitPath ?? inheritedFrom.orgUnitName ?? globalLabel;
}
